'use client';

import React from 'react';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Link from '@mui/material/Link';
import { CTAButton } from '@/components/CTAButton';
import { useObeUrl } from '@/hooks/useObeUrl';
import analytics from '@/services/analytics';
import { CmsDeal } from '@/services/cms/models/CmsDeal';
import { CardDescription } from './components/CardDescription';
import { CardImage } from './components/CardImage';
import { CardTitle, CardTitleProps } from './components/CardTitle';

export interface DealCardProps {
    deal: CmsDeal;
}

export interface DealCtaLink {
    ctaLabel: string;
    ctaLink: string | null | undefined;
}

export const DealCard = ({ deal }: DealCardProps) => {
    const isBookNow = deal.type === 'vehicle-relocation' || !deal.marketingUrl;
    const ctaLabel = isBookNow ? 'Book now' : 'Check deal';
    const bookNowLink = useObeUrl();
    if (deal.id) {
        bookNowLink.searchParams.set('dealId', deal.id);
    }

    const ctaLink = isBookNow ? bookNowLink.href : deal.marketingUrl;
    const image = deal.imgUrl ? <CardImage src={deal.imgUrl} alt={deal.title || deal.code || ''} /> : null;
    const cardTitleProps: CardTitleProps<typeof Link> =
        deal.title && ctaLink
            ? {
                  color: 'inherit',
                  component: Link,
                  href: ctaLink,
                  variant: 'subtitle2',
              }
            : {};

    return (
        <Card
            elevation={0}
            sx={{ alignSelf: 'flex-start' }}
            onClick={() =>
                analytics.plugins.interactions.trackInteraction({
                    source: 'Deal card',
                    action: 'click',
                    label: deal.code || deal.title,
                })
            }
        >
            {image && ctaLink ? <Link href={ctaLink}>{image}</Link> : image}
            <CardContent sx={{ px: 0 }}>
                <CardTitle sx={{ height: '60px' }} {...cardTitleProps}>
                    {deal.title}
                </CardTitle>
                <CardDescription>{deal.summary}</CardDescription>
            </CardContent>
            <DealCardActions ctaLabel={ctaLabel} ctaLink={ctaLink} />
        </Card>
    );
};

const DealCardActions = ({ ctaLabel, ctaLink }: DealCtaLink) =>
    ctaLink ? (
        <CardActions sx={{ px: 0 }}>
            <CTAButton href={ctaLink} variant="contained">
                {ctaLabel}
            </CTAButton>
        </CardActions>
    ) : null;
